import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState(null);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        console.error('No session_id found in URL hash');
        setStatus('error');
        setError('No session ID found. Please try again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      const sessionId = sessionIdMatch[1];

      try {
        setStatus('authenticating');
        
        // Call our backend which handles the Emergent API call server-side (avoids CORS)
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/auth/callback`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ session_id: sessionId })
          }
        );

        if (!response.ok) {
          let errorMessage = 'Authentication failed';
          try {
            const errorData = await response.json();
            errorMessage = errorData.detail || errorData.message || errorMessage;
          } catch {
            // Response wasn't JSON, use status text
            errorMessage = `Authentication failed (${response.status})`;
          }
          console.error('Auth callback failed:', response.status, errorMessage);
          throw new Error(errorMessage);
        }

        const userData = await response.json();
        
        // Store session token for Authorization header fallback (iPad Safari cookie issues)
        if (userData.session_token) {
          localStorage.setItem('firstcut_session_token', userData.session_token);
        }
        
        // Ensure userData is a plain object (no non-serializable properties)
        const cleanUserData = {
          user_id: userData.user_id,
          email: userData.email,
          name: userData.name,
          picture: userData.picture || null,
          subscription_tier: userData.subscription_tier || 'free',
          free_credits_used: userData.free_credits_used || 0,
          subscription_credits_remaining: userData.subscription_credits_remaining || 0,
          has_accepted_consent: userData.has_accepted_consent || false,
          consent_tos_version: userData.consent_tos_version || null
        };
        
        // Set user in context
        setUser(cleanUserData);
        
        // Store in localStorage as backup for immediate access
        localStorage.setItem('firstcut_user', JSON.stringify(cleanUserData));
        
        // Mark that we just completed auth - prevents race condition on redirect
        sessionStorage.setItem('just_authenticated', 'true');
        sessionStorage.setItem('login_time', Date.now().toString());
        
        setStatus('success');
        
        // Clear the hash from URL and navigate
        window.history.replaceState(null, '', '/dashboard');
        
        // Use navigate instead of window.location to preserve React state
        setTimeout(() => {
          navigate('/dashboard', { replace: true, state: { user: cleanUserData } });
        }, 100);
        
      } catch (err) {
        console.error('Auth callback error:', err);
        setStatus('error');
        // Safely extract error message as a string
        const errorMessage = typeof err === 'string' 
          ? err 
          : (err?.message || 'Authentication failed. Please try again.');
        setError(errorMessage);
        setTimeout(() => navigate('/'), 3000);
      }
    };

    processAuth();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        {status === 'error' ? (
          <>
            <div className="text-red-500 text-5xl mb-4">⚠️</div>
            <p className="text-foreground font-medium">Authentication Failed</p>
            <p className="text-muted-foreground text-sm mt-2">{error}</p>
            <p className="text-muted-foreground/60 text-xs mt-4">Redirecting to home...</p>
          </>
        ) : (
          <>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">
              {status === 'authenticating' && 'Verifying your identity...'}
              {status === 'success' && 'Success! Redirecting...'}
              {status === 'processing' && 'Signing you in...'}
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export default AuthCallback;
